import cn from 'classnames';
import { FC, ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { Icon, ProgressBar } from '@/shared/ui';

import type { DocumentHistory } from '../config';

import styles from './DocumentToolbar.module.scss';
import { RowTool } from './NoteRowTools';

type Props = {
  // Absent while the document may not be changed.
  history?: DocumentHistory;
  // Set while the template is edited: that it is, and how the change is
  // saved. The label is announced once the edit mode opens.
  editLabel?: ReactNode;
  editLabelDataComponent?: string;
  // How much of the document is filled; a template read without a note has
  // nothing to fill.
  progress?: { filled: number; total: number; label: string };
  // The document's tools after its undo and redo.
  children?: ReactNode;
  className?: string;
};

// The one bar of a document, kept in sight while the document scrolls: the
// undo and redo of the whole document, how much of it is filled, and its
// tools.
export const DocumentToolbar: FC<Props> = ({
  history,
  editLabel,
  editLabelDataComponent,
  progress,
  children,
  className,
}) => {
  const { formatMessage } = useIntl();

  return (
    <div
      className={cn(styles.bar, !!editLabel && styles.editing, className)}
      data-component="workspace-document-toolbar"
    >
      {history && (
        <span
          className={styles.history}
          data-component="workspace-document-history"
        >
          <RowTool
            icon="editorUndo"
            head
            label={formatMessage({
              id: 'workspace.template.undo',
              defaultMessage: 'Undo',
            })}
            disabled={!history.canUndo}
            onClick={history.undo}
          />
          <RowTool
            icon="editorRedo"
            head
            label={formatMessage({
              id: 'workspace.template.redo',
              defaultMessage: 'Redo',
            })}
            disabled={!history.canRedo}
            onClick={history.redo}
          />
        </span>
      )}
      {editLabel && (
        <p
          role="status"
          className={cn('p4', styles.label)}
          data-component={editLabelDataComponent}
        >
          <Icon className={styles.icon} name="pen" size={14} />
          <span>{editLabel}</span>
        </p>
      )}
      {progress && (
        <span
          className={styles.progress}
          data-component="workspace-document-progress"
        >
          {progress.label && (
            <span className={cn('p4', styles.progressText)}>
              {progress.label}
            </span>
          )}
          <ProgressBar
            className={styles.progressBar}
            height={5}
            value={
              progress.total ? (progress.filled / progress.total) * 100 : 0
            }
            ariaLabel={progress.label || undefined}
          />
        </span>
      )}
      {children}
    </div>
  );
};
